import * as React from 'react';
import { WifiOff, Wifi, AlertTriangle } from 'lucide-react';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { cn } from '@/lib/utils';

interface NetworkStatusIndicatorProps {
  className?: string;
  showWhenOnline?: boolean;
}

export const NetworkStatusIndicator: React.FC<NetworkStatusIndicatorProps> = ({
  className,
  showWhenOnline = false
}) => {
  const networkStatus = useNetworkStatus();

  if (networkStatus.isOnline && !showWhenOnline && !networkStatus.isSlowConnection) {
    return null;
  }

  const getStatusInfo = () => {
    if (!networkStatus.isOnline) {
      return {
        icon: WifiOff,
        text: 'Offline',
        className: 'bg-destructive text-destructive-foreground',
        description: 'No internet connection'
      };
    }

    if (networkStatus.isSlowConnection) {
      return {
        icon: AlertTriangle,
        text: 'Slow Connection',
        className: 'bg-warning text-warning-foreground',
        description: `Connection speed: ${networkStatus.effectiveType || 'Unknown'}`
      };
    }

    return {
      icon: Wifi,
      text: 'Online',
      className: 'bg-success text-success-foreground',
      description: `Good connection (${networkStatus.effectiveType || 'Unknown'})`
    };
  };

  const { icon: Icon, text, className: statusClassName, description } = getStatusInfo();

  return (
    <div className={cn(
      'fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium shadow-lg transition-all duration-200',
      statusClassName,
      className
    )}>
      <Icon className="h-4 w-4" />
      <span>{text}</span>
      
      {/* Tooltip for additional info */}
      <div className="sr-only" aria-live="polite">
        {description}
      </div>
    </div>
  );
};